import {openNewDownloadModal} from '@Modals/download-modal/download-modal.library';
import {getIsAiChatPremiumOnly, showAiChatPremiumOnlyFeatureDialog, showPurchaseCreditsDialog} from '@Libraries/ai-chat.library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {ImageQualityOption} from '@Components/download-panel/components/ai-chat-image-download-panel-content/ai-chat-image-download-panel-content.types';
import {getTeamCredits, refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {isUserPremium} from '@Libraries/user.library';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {AiChatCostType} from '@Libraries/ai-chat.library';
import {dispatchAiChatAssetDownloaded} from '@Components/ai-chat-window/lib/ai-chat-asset-downloaded.library';

export enum AiChatImageDownloadEvent {
  STARTED = 'ai-chat-image-download-started',
  COMPLETED = 'ai-chat-image-download-completed',
}

export interface AiChatImageDownloadContext {
  aiChatImageId: string;
  chatId: string;
}

export interface AiChatImageDownloadStartedEventDetail {
  chatId: string;
  hashedId: string;
}

export interface AiChatImageDownloadCompletedEventDetail {
  chatId: string;
  hashedId: string;
  isEnhanced: boolean;
  success: boolean;
}

export const openAiChatImageDownloadModal = (aiChatImageId: string, chatId: string): void => {
  openNewDownloadModal({
    id: 'ai-chat-image-download',
    aiChatImageDownloadContext: {
      aiChatImageId,
      chatId,
    },
    modalClassName: 'radius-12',
  });
};

export const checkIfAiChatImageHasUpscaledVersion = async (imageHashedId: string): Promise<boolean> => {
  const result = (await window.PMW.readLocal('ai/checkIfAiChatImageHasUpscaledVersion', {imageHashedId})) as {hasUpscaledVersion: boolean};
  return result.hasUpscaledVersion;
};

const trackDownloadAnalytics = (fileType: FileTypeOptions, isEnhanced: boolean): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_DOWNLOAD, {
    [GA4EventParamName.TYPE]: fileType === FileTypeOptions.PNG ? 'PNG' : 'PDF',
    [GA4EventParamName.VALUE]: isEnhanced ? 'enhanced' : 'original',
  });
};

const ensureUpscaleCredits = async (): Promise<boolean> => {
  const isAiChatPremiumOnly = await getIsAiChatPremiumOnly();
  const isPremiumUser = isUserPremium();

  if (isAiChatPremiumOnly && !isPremiumUser) {
    showAiChatPremiumOnlyFeatureDialog(true);
    return false;
  }

  await refreshTeamCreditsInStore();
  const {credits} = await getTeamCredits();
  if (credits < (AiChatCostType.UPSCALE_IMAGE_COST as number)) {
    const purchaseData = await showPurchaseCreditsDialog();

    if (!purchaseData?.creditsV2) {
      return false;
    }

    if (purchaseData.creditsV2 < (AiChatCostType.UPSCALE_IMAGE_COST as number)) {
      return false;
    }
  }

  return true;
};

const triggerBrowserDownloadFromUrl = async (url: string, filename: string): Promise<void> => {
  const response = await fetch(url, {mode: 'cors'});
  if (!response.ok) {
    throw new Error(`Failed to download AI chat image: HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);
};

const notifyServerOfDownload = (hashedId: string): void => {
  void window.PMW.writeLocal('ai/handleAiChatImageAssetDownload', {
    imageHashedId: hashedId,
  }).catch((error: unknown) => {
    console.error('Failed to create poster for downloaded image:', error);
  });
};

const fetchAndSaveAsset = async (hashedId: string, quality: ImageQualityOption, fileType: FileTypeOptions): Promise<void> => {
  const result = (await window.PMW.writeLocal('ai/getAiChatAssetDownloadUrl', {
    imageHashedId: hashedId,
    imageQuality: quality,
    fileType,
  })) as {url: string};

  await triggerBrowserDownloadFromUrl(result.url, getDownloadFileNameForGeneratedImageAsset(hashedId, fileType));
  notifyServerOfDownload(hashedId);
};

const dispatchDownloadStartedEvent = (chatId: string, hashedId: string): void => {
  window.dispatchEvent(
    new CustomEvent<AiChatImageDownloadStartedEventDetail>(AiChatImageDownloadEvent.STARTED, {
      detail: {chatId, hashedId},
    })
  );
};

const dispatchDownloadCompletedEvent = (chatId: string, hashedId: string, isEnhanced: boolean, success: boolean): void => {
  window.dispatchEvent(
    new CustomEvent<AiChatImageDownloadCompletedEventDetail>(AiChatImageDownloadEvent.COMPLETED, {
      detail: {chatId, hashedId, isEnhanced, success},
    })
  );
};

/** Tells the user an AI chat download failed. */
export const showAiChatDownloadFailedGrowl = (): void => {
  showMessageGrowl({
    text: window.i18next.t('pmwjs_something_went_wrong'),
    key: 'downloadedAiChatModelAssetError',
    interval: 3000,
    showCloseIcon: true,
    type: GROWL_TYPE.DANGER,
  });
};

interface RunDownloadWithNotificationsParams {
  chatId: string;
  hashedId: string;
  isEnhanced: boolean;
  performDownload: () => Promise<void>;
}

const runDownloadWithNotifications = async ({chatId, hashedId, isEnhanced, performDownload}: RunDownloadWithNotificationsParams): Promise<void> => {
  let isStartEventDispatched = false;
  let isSuccess = false;

  try {
    dispatchDownloadStartedEvent(chatId, hashedId);
    isStartEventDispatched = true;

    showLoading('downloadingAiChatModelAsset', {
      text: window.i18next.t('pmwjs_downloading'),
    });

    await performDownload();

    showMessageGrowl({
      text: window.i18next.t('pmwjs_file_downloaded'),
      key: 'downloadedAiChatModelAsset',
      interval: 2000,
      showCloseIcon: true,
      type: GROWL_TYPE.SUCCESS,
    });

    isSuccess = true;
    dispatchAiChatAssetDownloaded(chatId);
  } catch (error: unknown) {
    console.error('Failed to download AI chat image:', error);
    showAiChatDownloadFailedGrowl();
  } finally {
    hideLoading('downloadingAiChatModelAsset');
    if (isStartEventDispatched) {
      dispatchDownloadCompletedEvent(chatId, hashedId, isEnhanced, isSuccess);
    }
  }
};

export const downloadGeneratedImageAsset = async (chatId: string, hashedId: string, quality: ImageQualityOption, fileType: FileTypeOptions): Promise<void> => {
  const isEnhanced = quality === ImageQualityOption.ENHANCED_4K;
  const isAlreadyUpscaled = isEnhanced ? await checkIfAiChatImageHasUpscaledVersion(hashedId) : false;

  trackDownloadAnalytics(fileType, isEnhanced);

  if (isEnhanced && !isAlreadyUpscaled) {
    const canProceed = await ensureUpscaleCredits();
    if (!canProceed) {
      return;
    }
  }

  await runDownloadWithNotifications({
    chatId,
    hashedId,
    isEnhanced,
    performDownload: () => fetchAndSaveAsset(hashedId, quality, fileType),
  });
};

const getDownloadFileNameForGeneratedImageAsset = (hashedId: string, fileType: FileTypeOptions = FileTypeOptions.PNG): string => {
  const prefix = fileType === FileTypeOptions.PDF ? 'PosterMyWall_GeneratedDocument_' : 'PosterMyWall_GeneratedImage_';
  const trimmedHashedId = hashedId.length > 8 ? hashedId.substring(0, 8) : hashedId;
  const extension = fileType === FileTypeOptions.PDF ? 'pdf' : 'png';

  return `${prefix}${trimmedHashedId}.${extension}`;
};
